import { useEffect, useMemo, useState } from 'react'
import { Printer, AlertTriangle } from 'lucide-react'

/**
 * Standalone, print-optimised A4 sheet for a ปกส. reconciliation result.
 * Opened in a NEW TAB from /reports/sso-import.
 *
 * WHY A SEPARATE ROUTE INSTEAD OF window.print() ON THE PAGE. Printing the page itself
 * prints the whole app — the sidebar, the nav, the upload card — because those live in
 * <Layout> and no amount of .no-print inside one page reaches them. Same reason
 * AppointmentPrint.jsx exists. This route is registered OUTSIDE the Layout block, so
 * what you see is what comes out of the printer.
 *
 * HOW THE DATA GETS HERE. The result is handed over through localStorage, not through
 * the URL and not by re-running the diff: re-running would need the PDF again (it was
 * never uploaded anywhere) and the payload is far too big for a query string.
 * sessionStorage is per-tab and would arrive empty. The entry is bounded by age rather
 * than deleted on read — see readReport() for why deleting it broke the page outright.
 *
 * Typography is deliberately a notch below the screen: this sheet is filed, and more
 * rows per page is worth more than comfortable type.
 */

const STORAGE_KEY = 'sso-import:print'

// Anything older than this is a leftover from a previous session, not this print.
const MAX_AGE_MS = 2 * 60 * 60 * 1000

/**
 * ⚠️ DO NOT DELETE THE KEY ON READ. The obvious version of this — read it, then
 * localStorage.removeItem so patient names do not linger — is broken twice over:
 *
 *   1. <React.StrictMode> mounts, unmounts and remounts in dev, so the effect runs
 *      TWICE. The first run consumed the key; the second found nothing and replaced
 *      the good data with "ไม่พบข้อมูลรายงาน".
 *   2. It also breaks an ordinary browser refresh of this tab, in dev and in prod
 *      alike — one Ctrl+R and the report is gone with no way back but re-running the
 *      whole diff.
 *
 * So the entry is left in place and bounded by AGE instead. Being honest about what
 * that costs: the parsed report sits in this browser's localStorage until it expires
 * or the next print overwrites it. That is the same machine, in the same CRM, showing
 * the same names on screen — it is not a new exposure, and it is not a security
 * boundary either. A stale entry is purged the moment it is found.
 */
function readReport() {
  let raw
  try { raw = localStorage.getItem(STORAGE_KEY) } catch { return null }
  if (!raw) return null
  let parsed
  try { parsed = JSON.parse(raw) } catch { return null }
  if (!parsed?.result) return null
  if (!parsed.savedAt || Date.now() - parsed.savedAt > MAX_AGE_MS) {
    try { localStorage.removeItem(STORAGE_KEY) } catch { /* nothing to do */ }
    return null
  }
  return parsed
}

const PRINT_CSS = `
@page { size: A4 portrait; margin: 9mm; }
@media print {
  .no-print { display: none !important; }
  html, body { background: #fff !important; }
  body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .sheet { box-shadow: none !important; border: 0 !important; margin: 0 !important; padding: 0 !important; max-width: none !important; }
}
.sheet { font-size: 9px; line-height: 1.4; color: #000; }
.sheet h1 { font-size: 13px; font-weight: 700; margin: 0 0 1px; }
.sheet h2 { font-size: 10px; font-weight: 700; margin: 7px 0 1px; }
.sheet table { width: 100%; border-collapse: collapse; }
.sheet th, .sheet td { padding: 1.5px 4px; text-align: left; vertical-align: top; }
.sheet thead { display: table-header-group; }   /* repeat the header on every page */
.sheet tr { break-inside: avoid; }
.sheet thead th { border-bottom: 0.6px solid #000; font-weight: 600; white-space: nowrap; }
.sheet tbody tr { border-bottom: 0.3px solid #d4d4d4; }
.sheet section { break-inside: auto; margin-bottom: 6px; }
.sheet .num { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
.sheet .grp td { font-weight: 700; padding-top: 3px; border-bottom: 0.4px solid #999; }
`

const TH_MON = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.']
const thDate = (iso) => {
  if (!iso) return '—'
  const [y, m, d] = String(iso).split('-').map(Number)
  if (!y) return String(iso)
  return `${d} ${TH_MON[m - 1]} ${(y + 543) % 100}`
}
const fmt = (n) => Math.round(Number(n) || 0).toLocaleString('en-US')
const sum = (a) => a.reduce((t, r) => t + (Number(r.amount) || 0), 0)

export default function SsoImportPrint() {
  const [data, setData] = useState(undefined)   // undefined = loading, null = nothing

  // Which sections make it onto the paper. All on by default so nothing is silently
  // missing from a filed document; untick what this particular print does not need.
  const [show, setShow] = useState({
    summary: true, unpaid: true, unmatched: true, ambiguous: true,
    matched: true, repairs: true, paid_elsewhere: true,
  })

  useEffect(() => {
    document.title = 'รายงานการเทียบตามเบิก ปกส.'
    setData(readReport())
  }, [])

  const result = data?.result
  const header = data?.header
  const t = result?.totals

  const branchName = (id) => (result?.branches ?? []).find((b) => b.id === id)?.name ?? id

  /* Re-derived here rather than shipped pre-computed, so the sheet cannot drift from
   * the rules the page commits by. Same test as SsoImport: a row is only payable if the
   * report gave it BOTH a voucher and a transfer date — being listed is not being paid. */
  const isPaidInReport = (m) => !!m.voucher && !!m.transfer_date
  // REPORT_01 has no payment columns, so "not paid in this report" says nothing about ปกส.
  const noVoucherColumn = header?.has_voucher_column === false
  const sel = data?.selection ?? { idcards: [], names: [] }
  const selIds = useMemo(() => new Set(sel.idcards ?? []), [data])
  const selNames = useMemo(() => new Set(sel.names ?? []), [data])

  const unwritten = useMemo(
    // ⚠️ Mirrors SsoImport.jsx — `partial` and `rejected` are closed, not "to write".
    () => (result?.matched ?? []).filter((m) => !['received', 'partial', 'rejected'].includes(String(m.advance_status ?? ''))),
    [result],
  )
  const toWrite = useMemo(() => unwritten.filter(isPaidInReport), [unwritten])
  const notYetPaid = useMemo(() => unwritten.filter((m) => !isPaidInReport(m)), [unwritten])

  const voucherGroups = useMemo(() => {
    const g = new Map()
    for (const m of toWrite) {
      const k = `${m.voucher}|${m.transfer_date}`
      if (!g.has(k)) g.set(k, { voucher: m.voucher, transfer_date: m.transfer_date, rows: [] })
      g.get(k).rows.push(m)
    }
    return [...g.values()].map((x) => ({ ...x, amount: sum(x.rows) }))
      .sort((a, b) => String(a.transfer_date).localeCompare(String(b.transfer_date)))
  }, [toWrite])

  const repairable = useMemo(
    () => (result?.matched ?? []).filter((m) => m.backfill_idcard || m.name_mismatch),
    [result],
  )

  const unpaidByDate = useMemo(() => {
    const g = new Map()
    for (const u of result?.unpaid ?? []) {
      if (!g.has(u.service_date)) g.set(u.service_date, [])
      g.get(u.service_date).push(u)
    }
    return [...g.entries()]
  }, [result])

  if (data === undefined) return <div className="p-8 text-slate-500 text-[13px]">กำลังโหลด…</div>

  if (!result) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-8">
        <div className="max-w-[420px] bg-white rounded-2xl p-6 text-center shadow">
          <AlertTriangle className="mx-auto text-amber-500 mb-2" size={26} />
          <div className="text-[15px] font-semibold text-slate-900 mb-1">ไม่พบข้อมูลรายงาน</div>
          <p className="text-[13px] text-slate-600">
            หน้านี้เปิดจากปุ่ม “พิมพ์ / บันทึก PDF” ในหน้านำเข้ารายงาน ปกส. เท่านั้น
            — กรุณากลับไปกดปุ่มนั้นอีกครั้ง (ข้อมูลจะหมดอายุหลังจาก 2 ชั่วโมง)
          </p>
        </div>
      </div>
    )
  }

  const Toggle = ({ k, label, n }) => (
    <label className="inline-flex items-center gap-1.5 text-[12.5px] text-slate-700 cursor-pointer">
      <input type="checkbox" checked={show[k]} onChange={() => setShow((s) => ({ ...s, [k]: !s[k] }))}
        className="w-3.5 h-3.5 accent-slate-700" />
      {label} <span className="text-slate-400">({fmt(n)})</span>
    </label>
  )

  // Prefix dropped; only the half that actually differs is bold. Same reasoning as the
  // screen: the CRM keeps the prefix in its own column, so it is never what disagrees.
  const ReportName = ({ m }) => (
    <span>
      <span style={m.name_differs ? { fontWeight: 700 } : undefined}>{m.pdf_name}</span>
      {m.pdf_surname ? <> <span style={m.surname_differs ? { fontWeight: 700 } : undefined}>{m.pdf_surname}</span></> : null}
    </span>
  )

  const Table = ({ cols, rows }) => (
    <table>
      <thead><tr>{cols.map((c, i) => <th key={i} className={c.num ? 'num' : ''}>{c.label}</th>)}</tr></thead>
      <tbody>
        {rows.map((r, i) => <tr key={i}>{cols.map((c, j) => <td key={j} className={c.num ? 'num' : ''}>{c.get(r)}</td>)}</tr>)}
      </tbody>
    </table>
  )

  return (
    <div className="min-h-screen bg-slate-100 py-6 px-4 print:bg-white print:p-0">
      <style>{PRINT_CSS}</style>

      {/* toolbar — screen only */}
      <div className="no-print max-w-[794px] mx-auto mb-3 flex flex-wrap items-center gap-x-4 gap-y-2 bg-white rounded-xl border border-slate-200 px-4 py-3">
        <button type="button" onClick={() => window.print()}
          className="inline-flex items-center gap-1.5 rounded-lg bg-slate-800 hover:bg-slate-900 text-white px-3.5 py-2 text-[13px] font-medium">
          <Printer size={15} /> พิมพ์ / บันทึก PDF
        </button>
        <span className="text-[12px] text-slate-500">เลือกหัวข้อที่จะพิมพ์:</span>
        <Toggle k="summary" label="สรุปการบันทึก" n={voucherGroups.length} />
        <Toggle k="unpaid" label="ยังไม่ได้รับเงิน" n={result.unpaid.length} />
        <Toggle k="unmatched" label="ไม่พบในโปรแกรม" n={result.unmatched.length} />
        <Toggle k="ambiguous" label="ต้องเลือกเอง" n={result.ambiguous.length} />
        <Toggle k="matched" label="ตรงกัน" n={result.matched.length} />
        <Toggle k="repairs" label="แก้ประวัติคนไข้" n={repairable.length} />
        <Toggle k="paid_elsewhere" label="รับแล้วนอกรายงาน" n={(result.paid_elsewhere ?? []).length} />
        <span className="text-[11.5px] text-slate-400 basis-full">
          ในกล่องพิมพ์ของเบราว์เซอร์ เลือกปลายทางเป็น “Save as PDF” เพื่อบันทึกเป็นไฟล์
        </span>
      </div>

      <div className="sheet max-w-[794px] mx-auto bg-white rounded-lg shadow-lg print:shadow-none p-6 print:p-0">
        <h1>รายงานการเทียบรายการตามเบิก — สำนักงานประกันสังคม</h1>
        <div style={{ marginBottom: 5 }}>
          รหัสสถานพยาบาล {result.ssoclinic}
          {header?.clinicName ? ` · ${header.clinicName}` : ''}
          {' · '}สาขา: {(result.branches ?? []).map((b) => b.name).join(' · ') || '—'}
          {' · '}ช่วง {thDate(result.start_date)} – {thDate(result.end_date)}
        </div>
        <div style={{ marginBottom: 7 }}>
          รายงาน <b>{fmt(t.report_rows)}</b> รายการ <b>{fmt(t.report_amount)}</b> ฿
          {' · '}ตรงกัน <b>{fmt(t.matched)}</b> ({fmt(t.matched_amount)} ฿)
          {' · '}ยังไม่ได้รับเงิน <b>{fmt(t.unpaid)}</b> ({fmt(t.unpaid_amount)} ฿)
          {' · '}ไม่พบในโปรแกรม <b>{fmt(t.unmatched)}</b> ({fmt(t.unmatched_amount)} ฿)
          {' · '}ต้องเลือกเอง <b>{fmt(t.ambiguous)}</b>
          {t.paid_elsewhere ? <> · รับแล้วนอกรายงาน <b>{fmt(t.paid_elsewhere)}</b> ({fmt(t.paid_elsewhere_amount)} ฿)</> : null}
        </div>

        {show.summary && (voucherGroups.length || notYetPaid.length || selIds.size || selNames.size) ? (
          <section>
            <h2>สรุปการบันทึก</h2>
            {notYetPaid.length ? (
              <div style={{ marginBottom: 3 }}>
                <b>ไม่บันทึก {fmt(notYetPaid.length)} รายการ ({fmt(sum(notYetPaid))} ฿)</b> — {noVoucherColumn
                  ? 'รายงานการส่งเบิกไม่มีเลขที่ใบสำคัญรับเงินและวันที่โอน ต้องใช้รายงานการจ่าย (REPORT_02) ของงวดเดียวกันเพื่อบันทึกรับเงิน'
                  : 'อยู่ในรายงานแต่ยังไม่มีเลขที่ใบสำคัญรับเงินและวันที่โอน แปลว่า ปกส. รับเรื่องแล้วแต่ยังไม่จ่าย'}
              </div>
            ) : null}
            {voucherGroups.length ? (
              <table>
                <thead><tr><th>เลขที่ใบสำคัญรับเงิน</th><th>วันที่โอนเข้าบัญชี</th><th className="num">รายการ</th><th className="num">ยอดเงิน</th></tr></thead>
                <tbody>
                  {voucherGroups.map((g) => (
                    <tr key={g.voucher}>
                      <td>{g.voucher}</td><td>{thDate(g.transfer_date)}</td>
                      <td className="num">{fmt(g.rows.length)}</td><td className="num">{fmt(g.amount)} ฿</td>
                    </tr>
                  ))}
                  <tr style={{ fontWeight: 700 }}>
                    <td colSpan={2}>รวม</td>
                    <td className="num">{fmt(toWrite.length)}</td>
                    <td className="num">{fmt(sum(toWrite))} ฿</td>
                  </tr>
                </tbody>
              </table>
            ) : null}
            {selIds.size || selNames.size ? (
              <div style={{ marginTop: 3 }}>
                แก้ประวัติคนไข้: เติมเลขบัตร {fmt(selIds.size)} ราย · แก้ชื่อ {fmt(selNames.size)} ราย
              </div>
            ) : null}
          </section>
        ) : null}

        {show.unpaid && result.unpaid.length ? (
          <section>
            <h2>ยังไม่ได้รับเงิน — ทำแล้วแต่ยังไม่เคยบันทึกรับเงิน ({fmt(result.unpaid.length)} รายการ · {fmt(t.unpaid_amount)} ฿)</h2>
            <table>
              <thead><tr><th>HN</th><th>ชื่อ-สกุล</th><th>สาขา</th><th className="num">ยอด</th></tr></thead>
              {unpaidByDate.map(([date, rows]) => (
                <tbody key={date}>
                  <tr className="grp"><td colSpan={4}>{thDate(date)} · {fmt(rows.length)} รายการ · {fmt(sum(rows))} ฿</td></tr>
                  {rows.map((r) => (
                    <tr key={r.billing_id}>
                      <td>{r.hn || ''}</td><td>{r.crm_name || ''}</td>
                      <td>{branchName(r.branch_id)}</td><td className="num">{fmt(r.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              ))}
            </table>
          </section>
        ) : null}

        {show.unmatched && result.unmatched.length ? (
          <section>
            <h2>ไม่พบในโปรแกรม — อยู่ในรายงานแต่ไม่มีรายการตั้งเบิก ({fmt(result.unmatched.length)} รายการ · {fmt(t.unmatched_amount)} ฿)</h2>
            <Table
              cols={[
                { label: '#', num: true, get: (r) => r.seq },
                { label: 'วันที่', get: (r) => thDate(r.service_date) },
                { label: 'เลขบัตร', get: (r) => r.idcard },
                { label: 'ชื่อในรายงาน', get: (r) => r.raw_name },
                { label: 'ยอด', num: true, get: (r) => fmt(r.amount) },
                { label: 'หมายเหตุ', get: (r) => (r.patient_known ? 'มีคนไข้ ไม่มีรายการวันนั้น' : 'ไม่มีประวัติ') },
              ]}
              rows={result.unmatched}
            />
          </section>
        ) : null}

        {show.ambiguous && result.ambiguous.length ? (
          <section>
            <h2>ต้องเลือกเอง — เข้าได้หลายรายการ ({fmt(result.ambiguous.length)} รายการ)</h2>
            <Table
              cols={[
                { label: '#', num: true, get: (r) => r.seq },
                { label: 'วันที่', get: (r) => thDate(r.service_date) },
                { label: 'ชื่อในรายงาน', get: (r) => r.raw_name },
                { label: 'ตัวเลือก', get: (r) => r.candidates.map((c) => `${c.hn} ${c.crm_name}`).join(' | ') },
              ]}
              rows={result.ambiguous}
            />
          </section>
        ) : null}

        {show.matched && result.matched.length ? (
          <section>
            <h2>ตรงกัน ({fmt(result.matched.length)} รายการ · {fmt(t.matched_amount)} ฿)</h2>
            <Table
              cols={[
                { label: '#', num: true, get: (r) => r.seq },
                { label: 'วันที่', get: (r) => thDate(r.service_date) },
                { label: 'HN', get: (r) => r.hn || '' },
                { label: 'ชื่อ-สกุล', get: (r) => r.crm_name || r.raw_name },
                { label: 'ยอด', num: true, get: (r) => fmt(r.amount) },
                { label: 'ใบสำคัญรับเงิน', get: (r) => r.voucher || '—' },
                { label: 'วันที่โอน', get: (r) => (r.transfer_date ? thDate(r.transfer_date) : '—') },
                { label: 'สถานะ', get: (r) => ({ received: 'รับแล้ว', partial: 'รับบางส่วน', rejected: 'เบิกไม่ได้' }[String(r.advance_status ?? '')] ?? (r.voucher && r.transfer_date ? 'รอบันทึก' : noVoucherColumn ? 'รอรายงานการจ่าย' : 'ปกส.ยังไม่จ่าย')) },
              ]}
              rows={result.matched}
            />
          </section>
        ) : null}

        {show.repairs && repairable.length ? (
          <section>
            <h2>แก้ประวัติคนไข้จากรายงาน ({fmt(repairable.length)} ราย)</h2>
            <table>
              <thead>
                <tr>
                  <th>HN</th><th>ชื่อในโปรแกรม</th><th>ชื่อในรายงาน</th>
                  <th>เลขบัตร (เดิม → ใหม่)</th><th>เติมเลขบัตร</th><th>แก้ชื่อ</th>
                </tr>
              </thead>
              <tbody>
                {repairable.map((m) => (
                  <tr key={m.seq}>
                    <td>{m.hn || ''}</td>
                    <td>{m.crm_name || ''}</td>
                    <td>{m.name_mismatch ? <ReportName m={m} /> : 'เหมือนกัน'}</td>
                    <td>{m.backfill_idcard ? `${m.crm_idcard || 'ว่าง'} → ${m.idcard}` : 'ตรงกัน'}</td>
                    <td>{m.backfill_idcard ? (selIds.has(m.seq) ? '✓ บันทึก' : 'ไม่บันทึก') : '—'}</td>
                    <td>{m.name_mismatch ? (selNames.has(m.seq) ? '✓ บันทึก' : 'ไม่บันทึก') : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {t.idcard_conflict ? (
              <div style={{ marginTop: 3 }}>
                มี {fmt(t.idcard_conflict)} รายที่ในโปรแกรมมีเลขบัตร 13 หลักอยู่แล้วแต่ไม่ตรงกับรายงาน — ระบบไม่แก้ให้ ต้องตรวจด้วยตาก่อน
              </div>
            ) : null}
          </section>
        ) : null}

        {show.paid_elsewhere && (result.paid_elsewhere ?? []).length ? (
          <section>
            <h2>รับเงินแล้วแต่ไม่อยู่ในรายงานงวดนี้ ({fmt(result.paid_elsewhere.length)} รายการ · {fmt(t.paid_elsewhere_amount)} ฿)</h2>
            <Table
              cols={[
                { label: 'วันที่', get: (r) => thDate(r.service_date) },
                { label: 'HN', get: (r) => r.hn || '' },
                { label: 'ชื่อ-สกุล', get: (r) => r.crm_name || '' },
                { label: 'สาขา', get: (r) => branchName(r.branch_id) },
                { label: 'ยอด', num: true, get: (r) => fmt(r.amount) },
                { label: 'ใบสำคัญรับเงิน', get: (r) => r.claim_ref || '' },
              ]}
              rows={result.paid_elsewhere}
            />
          </section>
        ) : null}

        <div style={{ marginTop: 8, fontSize: 8, color: '#555' }}>
          พิมพ์เมื่อ {data.savedAt ? new Date(data.savedAt).toLocaleString('th-TH') : ''} · ระบบ Smile Seasons CRM
        </div>
      </div>
    </div>
  )
}
